import React from 'react';
import { style } from 'typestyle';
import { useTranslation } from 'react-i18next';
import PencilOutlineIcon from 'mdi-react/PencilOutlineIcon';
import * as communicationStyles from '../office-communication.styles';
import type { MessageStatus } from '../message-status';

interface Props {
  readonly status: MessageStatus;
  readonly title: string;
  readonly body?: string;
  readonly when: string;
  /** Extra marker such as "Non-working day" or "Recurring". */
  readonly tagLabel?: string;
  /** 0-100 for messages that are currently showing; omitted otherwise. */
  readonly progress?: number;
  readonly onOpen: () => void;
}

const STATUS_LABELS: Record<MessageStatus, string> = {
  active: 'message-status-live',
  scheduled: 'message-status-scheduled',
  sent: 'message-status-sent',
  past: 'message-status-finished',
};

// Presentation for one announcement or notification on the Communication tab. Opening it is the
// card's only action — editing and deleting stay in the existing modals.
const MessageCard = ({ status, title, body, when, tagLabel, progress, onOpen }: Props): React.JSX.Element => {
  const { t } = useTranslation();
  const isMuted = status === 'past' || status === 'sent';

  return (
    <div
      role="button"
      tabIndex={0}
      className={style(communicationStyles.messageCard(status, isMuted))}
      onClick={onOpen}
      onKeyDown={(event): void => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpen();
        }
      }}
    >
      <div className={style(communicationStyles.messageTop)}>
        <div className={style(communicationStyles.messageGrow)}>
          <span className={style(communicationStyles.statusChip(status))}>
            <span className={style(communicationStyles.statusDot(status))} />
            {t(STATUS_LABELS[status])}
          </span>
          {tagLabel && <span className={style(communicationStyles.tag)}>{tagLabel}</span>}
          <span className={`${style(communicationStyles.messageTitle)} ${communicationStyles.clampToTwoLines}`}>{title}</span>
          {body && <span className={`${style(communicationStyles.messageBody)} ${communicationStyles.clampToTwoLines}`}>{body}</span>}
        </div>
        {/* The whole card opens the edit modal; this only signals that it is editable. */}
        <span className={style(communicationStyles.editHint)}>
          <PencilOutlineIcon size={16} />
        </span>
      </div>
      {progress != null && (
        <div className={style(communicationStyles.progressBar)}>
          <span className={style(communicationStyles.progressFill(progress))} />
        </div>
      )}
      <span className={style(communicationStyles.messageWhen)}>{when}</span>
    </div>
  );
};

export default MessageCard;
